import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {Policy, ProductsEnum} from "@ferry/journey-services";
import {resourcesHelper, Footer} from "@ferry/portal-core";
import {formatDate} from "../../../CommonFunctions";
import {ButtonLink} from "../../../components/navigation/ButtonLink";
import FalconHeader from "../../../components/navigation/FalconHeader";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import {Types} from "../../../Types";
import type {ICustomerPortalService} from "../../../services/ICustomerPortalService";


interface IOldPoliciesPageProps {
}

interface IOldPoliciesPageState {
    isLoading: boolean;
    expiredPolicies: Policy[];
}

@authorize
export class OldPoliciesPage extends React.Component<IOldPoliciesPageProps, IOldPoliciesPageState> {
    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IOldPoliciesPageProps) {
        super(props);

        this.state = {
            isLoading: false,
            expiredPolicies: [],
        };
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Old Policies");

        this.setState({isLoading: true}, () => {
            this.customerPortalService.getOldPolicies().subscribe((policies: Policy[]) => {
                this.setState({
                    expiredPolicies: sortPoliciesByEndDateDesc(policies),
                    isLoading: false,
                });
            });
        });
    }

    public render() {
        return (
            <>
                <FalconHeader/>
                <Overlay show={this.state.isLoading}/>
                <div className="page-contents">
                    {this.pluginTemplates.renderBackLink("Back", true, `/dashboard`)}
                    <h1 className="page-heading-white">Your previous policies</h1>
                    {!this.state.isLoading &&
                        <div className="your-old-car-cover-panel-container">
                            {this.renderPolicies()}
                        </div>
                    }
                    {this.pluginTemplates.renderBackLink("Back", false, `/dashboard`)}
                </div>
                <Footer/>
            </>
        );
    }

    private renderPolicies = () => {

        if (this.state.expiredPolicies?.length < 1) {
            return (
                <div className="panel">
                    <p className="m-0 font-weight-extra-light">You don't have any previous policies</p>
                </div>
            );
        }

        return this.state.expiredPolicies.map((policy: Policy, index: number) => {
            const vehicles: any = [];
            for (const vehicle of policy.interaction.vehicles) {
                const isDriveAwayPolicy = ProductsEnum.DRIVE_AWAY === policy.products[0];
                const policySummaryLink = isDriveAwayPolicy ? `/dashboard/driveAwayOldPolicies/${policy.policyNumber}` : `/dashboard/annualMotorOldPolicies/${policy.policyNumber}`
                vehicles.push(
                    <div key={policy.policyNumber} id={`old-vehicle-${index}`}
                         className="section-panel your-old-car-cover-panel">
                        <div className="section car-details d-flex flex-row">
                            <div className="d-none d-md-block">
                                <img className="main-icon car-front-icon"
                                     src={`/${resourcesHelper.THEME_PATH}/img/car-icon-panel.svg`} alt="Vehicle icon"/>
                            </div>
                            <div className="d-flex flex-column">
                                <div className="d-md-none d-flex">
                                    <img className="main-icon car-front-icon"
                                         src={`/${resourcesHelper.THEME_PATH}/img/car-icon-panel.svg`} alt="Vehicle icon"/>
                                </div>
                                <div className="car-details-heading">{vehicle.make}, {vehicle.model}</div>
                                <div className="detail registration-number">
                                    <div id={`old-vehicle-details-${index}`} className="number-plate-preview">
                                        {vehicle.registrationNumber}
                                    </div>
                                </div>
                                <div className="policy-product">{isDriveAwayPolicy ? "5-Day Drive Away" : "Annual policy"}</div>
                                <div className="detail cover-start-date">
                                    <div className="detail-key">Cover start date:</div>
                                    <div id={`old-vehicle-start-date-${index}`}
                                         className="detail-value">{formatDate(policy.startDate, "DD MMM YYYY, HH:mm")}</div>
                                </div>
                                <div className="detail">
                                    <div className="detail-key">Cover end date:</div>
                                    <div id={`old-vehicle-end-date-${index}`}
                                         className="detail-value">{formatDate(policy.endDate, "DD MMM YYYY, HH:mm")}</div>
                                </div>
                                <div className="detail">
                                    <div className="detail-key">Cover type:</div>
                                    <div id={`old-vehicle-covel-level-${index}`}
                                         className="detail-value">Comprehensive</div>
                                </div>
                                <div className="section-links">
                                    <ButtonLink
                                        id={`old-vehicle-summary-btn-${index}`}
                                        routeState={{ interactionId:  policy.interaction._id}}
                                        primary={true}
                                        to={policySummaryLink}>
                                        View policy details
                                    </ButtonLink>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
            return vehicles;
        });
    };

}

export const sortPoliciesByEndDateDesc = (policies: Policy[]): Policy[] => {
    const endDateTimestamp = (policy: Policy): number => {
        const timestamp = Date.parse(policy.endDate as any);
        return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    return [...policies].sort((firstPolicy: Policy, secondPolicy: Policy) =>
        endDateTimestamp(secondPolicy) - endDateTimestamp(firstPolicy)
    );
};
